import { useEffect } from 'react'
import { type QueryClient, useQuery } from '@tanstack/react-query'
import { Outlet, createRootRouteWithContext } from '@tanstack/react-router'
import type { AuthContext } from '@/providers/auth-provider'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { CircleAlert, CircleCheck, Info, Loader2 } from 'lucide-react'
import { hasNewVersion, versionCheck } from '@/lib/version-check'
import { Toaster } from '@/components/ui/sonner'
import { SentryErrorBoundary } from '@/components/error-boundary'
import { NavigationProgress } from '@/components/navigation-progress'
import { GeneralError } from '@/features/errors/general-error'
import { NotFoundError } from '@/features/errors/not-found-error'

function RootComponent() {
  const { data: latestBuildId } = useQuery(versionCheck.current())
  const newVersionAvailable = hasNewVersion(latestBuildId)

  useEffect(() => {
    if (newVersionAvailable) {
      window.location.reload()
    }
  }, [newVersionAvailable])

  return (
    <SentryErrorBoundary>
      <NavigationProgress />
      <Outlet />
      <Toaster
        duration={5000}
        icons={{
          success: <CircleCheck className='text-success-foreground h-4 w-4' />,
          info: <Info className='text-muted-foreground h-4 w-4' />,
          warning: <CircleAlert className='text-warning-foreground h-4 w-4' />,
          error: <CircleAlert className='text-destructive h-4 w-4' />,
          loading: (
            <Loader2 className='text-muted-foreground h-4 w-4 animate-spin' />
          ),
        }}
        toastOptions={{
          className: 'custom-toast',
        }}
      />
      {import.meta.env.MODE === 'development' && (
        <>
          <ReactQueryDevtools buttonPosition='bottom-right' />
          <TanStackRouterDevtools position='bottom-right' />
        </>
      )}
    </SentryErrorBoundary>
  )
}

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient
  auth: AuthContext
}>()({
  component: RootComponent,
  notFoundComponent: NotFoundError,
  errorComponent: GeneralError,
})
